<template>
  <div class="ibox">
    <div class="ibox-content p-t-m">
      <div class="row">
        <div class="col-xs-12">
          <div class="cal-container relative">
            <div v-if="anyLoading">
              <slot name="spinner">
                <public-spinner></public-spinner>
              </slot>
            </div>
            <div v-if="calendarOptionsLoaded">
              <FullCalendar :options="calendarOptions" ref="calendar" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PublicSpinner from './public_spinner.vue';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import adaptivePlugin from '@fullcalendar/adaptive';

export default {
  name: 'public-calendar',
  components: {
    PublicSpinner,
    FullCalendar,
  },
  props: {
    eventRenderCallback: {
      type: Function,
      required: true,
    },
    timeViews: {
      type: String,
      default: 'dayGridMonth,timeGridWeek,listWeek,multiMonthYear',
    },
    defaultMobileView: {
      type: String,
      default: 'listWeek',
    },
    baseEventShowUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    const vm = this;
    return {
      calendarOptionsLoaded: false,
      error: false,
      errorMessage: '',
      initialView: 'dayGridMonth',
      initialDate: null,
      dualMeetLoading: false,
      dualMeetVisible: true,
      dualMeetSource: {
        id: 'dual_meet',
        events: (fetchInfo, successCallback, failureCallback) => {
          const params = `?event_type[]=dual_meet&event_type[]=scramble&start=${fetchInfo.startStr}&end=${fetchInfo.endStr}&preventCache=${new Date().toISOString()}`;
          const url = vm.getUrlWithParams(params);
          vm.dualMeetLoading = true;
          vm.$apiService.loadAllPages(url, 'events')
            .then((events) => {
              vm.dualMeetLoading = false;
              successCallback(vm.formatFCEvents(events));
            })
            .catch((error) => {
              vm.dualMeetLoading = false;
              vm.error = true;
              vm.errorMessage = error.toString();
              failureCallback(error);
            });
        },
      },

      otherLoading: false,
      otherVisible: true,
      otherSource: {
        id: 'other',
        events: (fetchInfo, successCallback, failureCallback) => {
          const params = `?event_type=other&start=${fetchInfo.startStr}&end=${fetchInfo.endStr}&preventCache=${new Date().toISOString()}`;
          const url = vm.getUrlWithParams(params);
          vm.otherLoading = true;
          vm.$apiService.loadAllPages(url, 'events')
            .then((events) => {
              vm.otherLoading = false;
              successCallback(vm.formatFCEvents(events));
            })
            .catch((error) => {
              vm.otherLoading = false;
              vm.error = true;
              vm.errorMessage = error.toString();
              failureCallback(error);
            });
        },
      },

      practiceLoading: false,
      practiceVisible: true,
      practiceSource: {
        id: 'practice',
        events: (fetchInfo, successCallback, failureCallback) => {
          const params = `?event_type=practice&start=${fetchInfo.startStr}&end=${fetchInfo.endStr}&preventCache=${new Date().toISOString()}`;
          const url = vm.getUrlWithParams(params);
          vm.practiceLoading = true;
          vm.$apiService.loadAllPages(url, 'events')
            .then((events) => {
              vm.practiceLoading = false;
              successCallback(vm.formatFCEvents(events));
            })
            .catch((error) => {
              vm.practiceLoading = false;
              vm.error = true;
              vm.errorMessage = error.toString();
              failureCallback(error);
            });
        },
      },

      tournamentLoading: false,
      tournamentVisible: true,
      tournamentSource: {
        id: 'tournament',
        events: (fetchInfo, successCallback, failureCallback) => {
          const params = `?event_type=tournament&start=${fetchInfo.startStr}&end=${fetchInfo.endStr}&preventCache=${new Date().toISOString()}`;
          const url = vm.getUrlWithParams(params);
          vm.tournamentLoading = true;
          vm.$apiService.loadAllPages(url, 'events')
            .then((events) => {
              vm.tournamentLoading = false;
              successCallback(vm.formatFCEvents(events));
            })
            .catch((error) => {
              vm.tournamentLoading = false;
              vm.error = true;
              vm.errorMessage = error.toString();
              failureCallback(error);
            });
        },
      },
    };
  },
  computed: {
    anyLoading() {
      const vm = this;
      return vm.dualMeetLoading || vm.otherLoading || vm.practiceLoading || vm.tournamentLoading;
    },
    calendarOptions() {
      return {
        plugins: [ dayGridPlugin, timeGridPlugin, listPlugin, multiMonthPlugin, interactionPlugin, adaptivePlugin ],
        initialDate: this.initialDate,
        initialView: this.initialView,
        datesSet: (info) => {
          // Update hash when calendar date/view changes
          const currentDate = info.view.currentStart;  // Use the currentStart date which represents the actual month being displayed
          const view = info.view;
          window.location.hash = `year=${currentDate.getFullYear()}&month=${currentDate.getMonth() + 1}&day=${currentDate.getDate()}&view=${view.type}`;
        },
        headerToolbar: {
          start: 'prev,next',
          center: 'title',
          end: this.timeViews
        },
        views: {
          dayGridMonth: {
            titleFormat: { year: 'numeric', month: 'short' }
          },
          timeGridWeek: {
            titleFormat: { year: 'numeric', month: 'short', day: '2-digit' }
          },
          listWeek: {
            titleFormat: { year: 'numeric', month: 'short', day: '2-digit' }
          }
        },
        buttonText: {
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
          list: 'List',
          multiMonthYear: 'Year',
        },
        dragRevertDuration: 250,
        selectLongPressDelay: 500,
        navLinks: true,
        nowIndicator: true,
        editable: false,
        selectable: false,
        timeZone: 'local',
        slotMinTime: '07:00:00',
        slotMaxTime: '23:00:00',
        scrollTime: '06:00:00',
        eventDidMount: (info) => {
          this.eventRenderCallback(info.event, info.el);
        },
        eventSources: [
          this.dualMeetSource,
          this.otherSource,
          this.practiceSource,
          this.tournamentSource,
        ],
        dayMaxEvents: true,
        contentHeight: 'auto',
        moreLinkClick: 'popover',
        schedulerLicenseKey: '0677983463-fcs-1739210166'
      }
    }
  },
  mounted() {
    const vm = this;
    // set the initial date and view
    this.initialDate = this.getInitialDateFromHash();

    let defaultView = this.getInitialViewFromHash();
    if ($(window).width() < 768) {
      defaultView = this.defaultMobileView;
    }
    vm.initialView = defaultView;
    this.calendarOptionsLoaded = true;
  },
  methods: {
    getUrlWithParams(params) {
      return `/api/team/v1/events${params}`;
    },
    formatFCEvents(events) {
      const fcEvents = [];
      events.forEach((returnedEvent) => {
        let start = returnedEvent.start_at;
        let end = returnedEvent.end_at;

        const allDay = returnedEvent.all_day;

        // If it is an all day event, the time becomes 'ambigious' meaning that we only deal with dates.
        if (allDay) {
          start = returnedEvent.start_at.split('T')[0];
          end = returnedEvent.end_at.split('T')[0];
        }

        const { color } = returnedEvent;

        const recurring = returnedEvent.has_repeated_events || returnedEvent.parent_id != null;

        const fcEvent = {
          id: returnedEvent.id,
          title: returnedEvent.name,
          allDay,
          start,
          end,
          color,
          recurring,
          location: returnedEvent.location,
          notes: returnedEvent.notes,
          event_type: returnedEvent.event_type,
          paid_session: returnedEvent.paid_session,
          url: `${this.baseEventShowUrl}/${returnedEvent.id}`,
        };
        fcEvents.push(fcEvent);
      });
      return fcEvents;
    },
    getInitialDateFromHash() {
      const today = new Date();
      let year = today.getFullYear();
      let month = today.getMonth();
      let day = today.getDate();

      // Parse hash parameters
      const hash = window.location.hash.substring(1);
      const params = hash.split('&');
      
      params.forEach(param => {
        const [key, value] = param.split('=');
        if (key === 'year') year = parseInt(value);
        if (key === 'month') month = parseInt(value) - 1;
        if (key === 'day') day = parseInt(value);
      });

      // Return null if any of the parsed values are invalid
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
      }

      // Validate month is between 0-11
      if (month < 0 || month > 11) {
        return null;
      }

      // Validate day is between 1-31
      if (day < 1 || day > 31) {
        return null;
      }

      return new Date(year, month, day);
    },
    getInitialViewFromHash() {
      const hash = window.location.hash.substring(1);
      const params = hash.split('&');
      let view = 'dayGridMonth';

      params.forEach(param => {
        const [key, value] = param.split('=');
        if (key === 'view') view = value;
      });

      return view;
    },
  },
};
</script>
